import { ApiError, MediumScreenModel } from "components";
import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import FeaturedAdsForm from "./form/FeaturedAdsApp";
import { connect, useSelector } from "react-redux";
import {
  createFeaturedAds,
  ResetFeaturedAds,
  updateFeaturedAds,
} from "../state/action";
import { FormDataValidation } from "./components/FormDataValidation";
import { FeaturedAdsEnum, FeaturedAdsStatusEnum } from "props/enum";
import HandleUploadFeaturedAdsVideo from "./HandleUploadFeaturedAdsVideo";
import { FeaturedAdsInput, FeaturedAdsInputError, FeaturedAdsProps, stateStatusType } from "../state/actionType";

interface OwnProps {
  featuredaAdsData: FeaturedAdsProps;
  onClose: () => void;
}
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = DispatchProps & OwnProps;
const HandleUpdateFeaturedAds: React.FC<Props> = ({
  featuredaAdsData,
  onClose,
  onResetFeaturedAds,
  onUpdateFeaturedAds,
}) => {
  const featuredAdsState = useSelector(
    (state: any) => state.featuredAds.genericFeaturedAdsState
  );
  const [posterUrl, setPosterUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [openUploadVideo, setUploadVideo] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [formData, setFormData] = useState<FeaturedAdsInput>({
    featured_type: featuredaAdsData?.featured_type,
    status: featuredaAdsData?.status,
    image: featuredaAdsData?.image,
    video: featuredaAdsData?.video,
    featured_order: featuredaAdsData?.featured_order,
    name: featuredaAdsData?.name,
  });
  const [formError, setFormError] = useState<FeaturedAdsInputError>({});
  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "featured_order" ? Number(value) : value,
    }));
    setFormError((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };
  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    const validationErrors = FormDataValidation(formData);
    if (Object.keys(validationErrors).length > 0) {
      setFormError(validationErrors);
      return;
    }
    setLoading(true);
    const input: FeaturedAdsInput = {
      name: formData.name,
      featured_type: formData.featured_type,
      status: formData.status,
      featured_order: formData.featured_order,
    };
    let uploadedFilename: string | undefined = undefined;
    if (imageFile) {
      const uploadUrl = `${process.env.REACT_APP_FEATURED_ADS_UPLOAD_IMAGE_BASE_URL}`;
      const form = new FormData();
      form.append("file", imageFile);
      const response = await fetch(uploadUrl ?? "", {
        method: "POST",
        body: form,
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Upload failed:", errorText);
        throw new Error("Failed to upload image");
      }

      const result = await response.json();
      uploadedFilename = result?.featuredImageUrl;

      if (!uploadedFilename) {
        console.error("Image upload response invalid", result);
        throw new Error("Image upload response invalid");
      }
    }

    if (
      formData.featured_type.trim() !== "" &&
      formData.name.trim() !== "" &&
      formData.featured_order !== 0
    ) {
      if (uploadedFilename) {
        input.image = uploadedFilename;
      }
      const ad_id = featuredaAdsData?.id?.toString();
      onUpdateFeaturedAds(input, ad_id);
      setSubmitted(true);
    }
    setLoading(false);
  };
  useEffect(() => {
    if (!featuredAdsState.isLoading && submitted) {
      if (featuredAdsState.error) {
        setApiError(featuredAdsState.error);
      } else if (
        featuredAdsState.status === stateStatusType.SUCCESS &&
        formData.featured_type === FeaturedAdsEnum.image
      ) {
        onClose();
        onResetFeaturedAds();
      } else {
        setApiError(null);
      }
    }
  }, [
    featuredAdsState.isLoading,
    featuredAdsState.error,
    featuredAdsState.status,
    submitted,
  ]);
useEffect(() => {
    const fetchVideoMeta = async () => {
      try {
        const videoId = featuredaAdsData?.video.toString();
        const url = `${process.env.REACT_APP_VDO_CIPHER_BASE_URL}${videoId}`;
        const response = await fetch(url, {
          method: "GET",
          headers: { Accept: "application/json" },
        });

        if (response.ok) {
          response.json().then((data) => {
            const thumbnail = data?.posters[0]?.url;
            setPosterUrl(thumbnail);
          });
        }
      } catch (err: any) {
        console.error("Failed to fetch thumbnail", err);
      }
    };
    fetchVideoMeta();
  }, []);
  useEffect(() => {
  if (featuredAdsState.status === stateStatusType.SUCCESS && submitted) {
    if (formData.featured_type === FeaturedAdsEnum.video) {
      if (videoFile) {
        setUploadVideo(true);
      } else {
        onClose();
        onResetFeaturedAds();
      }
    }
  }
}, [featuredAdsState.status, submitted, formData.featured_type, videoFile]);

  useEffect(() => {
    onResetFeaturedAds();
  }, []);
  return (
    <>
      {openUploadVideo ? (
        <>
          <HandleUploadFeaturedAdsVideo
            videoFile={videoFile}
            onClose={onClose}
          />
        </>
      ) : (
        <>
          <FeaturedAdsForm
            header={"Update Featured Ads"}
            formData={formData}
            formError={formError}
            handleChange={handleChange}
            handleSubmit={handleSave}
            loading={loading}
            onClose={onClose}
            handleVideoFileChange={setVideoFile}
            handleImageFileChange={setImageFile}
            isNewVideo={true}
            posterUrl={posterUrl}
          />
          {apiError && <ApiError>{apiError}</ApiError>}
        </>
      )}
    </>
  );
};
const mapDispatchToProps = (dispatch: any) => ({
  onUpdateFeaturedAds: (input: FeaturedAdsInput, ad_id: string) =>
    dispatch(updateFeaturedAds(input, ad_id)),
  onResetFeaturedAds: () => dispatch(ResetFeaturedAds()),
});
export default connect(null, mapDispatchToProps)(HandleUpdateFeaturedAds);
